import {
    BaseApiRequestPayload,
    GetCouponRatesRequestPayload,
    GetReservationRequestPayload,
    GetUnitAvailabilityRequestPayload,
    GetUnitListRequestPayload,
    PostMarketingSurveyRequestPayload,
    PostReservationFeeRequestPayload,
    PostReservationSaveDocumentsPayload,
    PostWizardInitRequestPayload,
    PostScaBillingInfoUpdate,
    PostScaMoveInPrepare,
    PostScaReservationWithFeePrepare,
    SearchReservationsRequestPayload,
} from '@r6digital/rapidstor-types/react/api/Requests';
import {
    Charge,
    chargeTransformer,
} from '@r6digital/rapidstor-types/react/entities/Charge';
import { corpTransformer } from '@r6digital/rapidstor-types/react/entities/Corp';
import Descriptor, { descriptorTransformer } from '@r6digital/rapidstor-types/react/entities/Descriptor';
import { locationTransformer } from '@r6digital/rapidstor-types/react/entities/Location';
import {
    groupByLocationCode,
    MarketingSource,
} from '@r6digital/rapidstor-types/react/entities/MarketingSource';
import {
    TrackEvent,
    TrackEventRequest,
} from '@r6digital/rapidstor-types/react/entities/Tracking';
import { unitTypeTransformer } from '@r6digital/rapidstor-types/react/entities/UnitType';
import { UnitList } from '@r6digital/rapidstor-types/react/rtk-query/UnitList';
import {
    RapidStorErrorResponse,
    RapidStorPersona,
    RapidStorResponse,
    RapidStorUnitAvailability,
    RapidStorUnitList,
    ScaPrepareResponse,
} from '@r6digital/rapidstor-types/types/Api';
import RapidStorCharge from '@r6digital/rapidstor-types/types/Charge';
import {
    ContactRequestPayload,
    ContactResponse,
} from '@r6digital/rapidstor-types/types/Contact';
import Corporation from '@r6digital/rapidstor-types/types/Corporation';
import { RapidStorCouponRatesResponse } from '@r6digital/rapidstor-types/types/Coupon';
import {
    RapidStorBoxShopCouponResponse,
    RapidStorMerchandise,
} from '@r6digital/rapidstor-types/types/Merchandise';
import {
    BaseReservation,
    CostRetrieveResponse,
    MovedInReservation,
    RapidStorWizardInit,
    RapidStorMoveInResponse,
    ReservationPatchRequest,
    RapidStorReservationRequest,
} from '@r6digital/rapidstor-types/types/Reservation';
import Site from '@r6digital/rapidstor-types/types/Site';

import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@/core/types/Http';
import {
    OAuthIntegration,
    OAuthIntegrationRequestPayload,
    OAuthIntegrationResponse,
} from '@/core/types/OAuthIntegration';
import { PersonaType } from '@/journey-based-listing/screens';

/**
 * Creates the base query lazily to ensure window.rapidstor_settings is available.
 * This prevents race conditions where the module is imported before initApplication completes.
 * The baseQuery function is called for each request, so we create fetchBaseQuery lazily at request time.
 */
let cachedBaseQuery: ReturnType<typeof fetchBaseQuery> | null = null;

const createBaseQuery = (): ReturnType<typeof fetchBaseQuery> => async (args, api, extraOptions) => {
    // Check settings at request time, not module load time
    if (!window.rapidstor_settings?.rapidstor_options?.baseURL) {
        throw new Error(
            'window.rapidstor_settings is not initialized. Ensure initApplication() has completed before using the API.',
        );
    }

    // Create the baseQuery lazily on first request
    if (!cachedBaseQuery) {
        cachedBaseQuery = fetchBaseQuery({
            baseUrl: window.rapidstor_settings.rapidstor_options.baseURL,

            prepareHeaders: (headers) => {
                if (!window.rapidstor_settings?.rapidstor_options) {
                    return headers;
                }

                if (
                    window.rapidstor_settings.rapidstor_options.locationCode
              && window.rapidstor_settings.rapidstor_options.corpCode
                ) {
                    headers.set(
                        'site',
                        window.rapidstor_settings.rapidstor_options.locationCode,
                    );
                    headers.set('corp', window.rapidstor_settings.rapidstor_options.corpCode);
                }
                return headers;
            },
        });
    }

    return cachedBaseQuery(args, api, extraOptions);
};

const coalesceDescriptors = (descriptors: Descriptor[]): Descriptor[] => {
    // If a descriptor is useForCarousel, and has no availabile units, and has an upgrade path,
    // then we want to show the upgrade path instead.
    const out: Descriptor[] = [];
    const descriptorMap = Object.fromEntries(descriptors.map((d) => [d._id, d]));
    for (const descriptor of descriptors) {
        if (
            descriptor.useForCarousel
      && !descriptor.unitTypes.find((ut) => ut.hasComputedAvailable)
      && descriptor.upgradesTo?.length
        ) {
            const firstAvailableUpgrade = descriptor.upgradesTo
                .map(({ _id }) => descriptorMap[_id as string])
                .filter((d) => d)
                .find((d) => d.unitTypes.find((ut) => ut.hasComputedAvailable));
            if (!firstAvailableUpgrade) {
                out.push(descriptor);
                continue;
            }
            out.push({
                ...firstAvailableUpgrade,
                useForCarousel: true,
                upgradesTo: descriptor.upgradesTo.filter(
                    ({ _id }) => _id !== firstAvailableUpgrade._id,
                ),
                ordinalPosition: descriptor.ordinalPosition,
            });
        } else {
            out.push(descriptor);
        }
    }
    // Remove duplicate ids and return.
    return out.filter((d, i) => i === out.findIndex((d0) => d0._id === d._id));
};

export const rapidstorApi = createApi({
    reducerPath: 'rapidstorApi',
    baseQuery: retry(createBaseQuery(), { maxRetries: 3 }),
    keepUnusedDataFor: 3600,
    tagTypes: ['Location', 'UnitList', 'Personas'],
    endpoints: (builder) => ({
        getCorp: builder.query({
            query: ({ corpCode }: { corpCode: string }) => ({
                url: `/api/v1/corp/${corpCode}`,
            }),
            transformResponse: (resp: RapidStorResponse<Corporation.Base>) => corpTransformer(resp.results),
        }),
        getLocationByCode: builder.query({
            query: ({ locationCode }) => `/api/v1/locations/${locationCode}`,
            transformResponse: (resp: RapidStorResponse<Site>) => locationTransformer(resp.results),
            providesTags: ['Location'],
        }),
        getUnitListByType: builder.query<UnitList, GetUnitListRequestPayload>({
            query: ({
                locationCodes, persona, unitTypeNames, unitTypeRegex,
            }) => ({
                url: '/api/v1/unit-list/by-type',
                method: HttpMethod.POST,
                body: {
                    sites: locationCodes,
                    persona,
                    unitTypeNames,
                    unitTypeRegex,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorUnitList>,
            ): UnitList => {
                if (
                    resp.results.descriptors.length === 0
          || resp.results.unitTypes.length === 0
                ) {
                    return {
                        descriptors: [],
                        unitTypes: [],
                    };
                }

                return {
                    descriptors: coalesceDescriptors(
                        resp.results.descriptors
                            .map((d): Descriptor => descriptorTransformer(d, undefined))
                            .filter((d) => d.unitTypes.length > 0 && !d.hidden && d.enabled),
                    ),
                    unitTypes: resp.results.unitTypes.map((u) => unitTypeTransformer(u, undefined)),
                };
            },
        }),
        getPersonas: builder.query<PersonaType[], BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/${locationCode}/personas`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorPersona[]>,
            ): PersonaType[] => resp.results.map((persona) => ({
                _id: persona._id,
                label: persona.name,
                filter: '',
                icon: persona.image || '',
                text: persona.description || '',
                available: persona.useForJourneySelection || false,
                customerType: persona.assignCustomerType || null,
                parameters: persona.parameters || [],
                deals: persona.deals || [],
            })),
            providesTags: ['Personas'],
        }),
        getReservation: builder.query<
        BaseReservation | MovedInReservation,
        GetReservationRequestPayload
        >({
            query: ({
                locationCode,
                token,
                corpCode,
            }: GetReservationRequestPayload) => ({
                url: `/api/v1/reservation/${token}`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<BaseReservation | MovedInReservation>,
            ): BaseReservation | MovedInReservation => resp.results,
        }),
        getReservationBySitelink: builder.query<
        BaseReservation,
        { locationCode: string; corpCode: string; id: string }
        >({
            query: ({ locationCode, corpCode, id }) => ({
                url: `/api/v1/reservation/sitelink/${id}`,
                method: HttpMethod.POST,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => resp.results,
        }),
        searchReservations: builder.query<(BaseReservation | MovedInReservation)[],
        SearchReservationsRequestPayload
        >({
                query: ({ locationCode, corpCode, ...options }) => ({
                    url: '/api/v1/reservations',
                    method: HttpMethod.GET,
                    headers: {
                        site: locationCode,
                        corp: corpCode,
                    },
                    params: options,
                }),
                transformResponse: (
                    resp: RapidStorResponse<(BaseReservation | MovedInReservation)[]>,
                ) => resp.results,
            }),
        postReservation: builder.mutation<
        BaseReservation,
        RapidStorReservationRequest
        >({
            query: ({
                locationCode,
                corpCode,
                payload,
            }: RapidStorReservationRequest) => ({
                url: '/api/v1/reservation',
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => resp.results,
        }),
        patchReservation: builder.mutation<
        BaseReservation,
        ReservationPatchRequest
        >({
            query: ({
                locationCode, corpCode, payload, token,
            }) => ({
                url: `/api/v1/reservations/${token}`,
                method: HttpMethod.PATCH,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<{ reservation: BaseReservation }>,
            ) => resp.results.reservation,
        }),
        postReservationFee: builder.mutation<
        BaseReservation,
        PostReservationFeeRequestPayload
        >({
            query: ({
                locationCode,
                corpCode,
                payload,
                token,
            }: PostReservationFeeRequestPayload) => ({
                url: `/api/v1/reservation/${token}/add-fee`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => resp.results,
        }),
        postSaveQuote: builder.mutation<
        BaseReservation,
        RapidStorReservationRequest
        >({
            query: ({ locationCode, corpCode, payload }) => ({
                url: '/api/v1/reservation/save-quote',
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => resp.results,
        }),
        postContact: builder.mutation<ContactResponse, ContactRequestPayload>({
            query: ({ locationCode, corpCode, payload }) => {
                const body = new FormData();
                body.append('location_code', locationCode);
                body.append('first_name', payload.first_name);
                body.append('last_name', payload.last_name);
                body.append('from_email', payload.from_email);
                body.append('phone', payload.phone);
                body.append('message', payload.message);

                return {
                    url: '/api/v1/contact',
                    method: HttpMethod.POST,
                    body,
                    headers: {
                        site: locationCode,
                        corp: corpCode,
                    },
                };
            },
            transformResponse: (resp: RapidStorResponse<ContactResponse>) => resp.results,
        }),
        postTrackEvent: builder.mutation<TrackEvent, TrackEventRequest>({
            query: ({ corpToken, locationToken, payload }) => ({
                url: `/rapidstor/${corpToken}/${locationToken}/api/track`,
                method: HttpMethod.POST,
                body: payload,
            }),
        }),
        getMoveInCostRetrieve: builder.mutation({
            query: ({ location, corpCode, reservationId }) => ({
                url: `/api/v1/move-in/${reservationId}/cost-retrieve`,
                method: HttpMethod.GET,
                headers: {
                    site: location,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<CostRetrieveResponse>) => resp.results,
            transformErrorResponse: (resp: {
                status: number;
                data: RapidStorErrorResponse<string>;
            }) => resp.data.error,
        }),
        getCharges: builder.query<Charge[], BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/locations/${locationCode}/charges`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorCharge[]>) => resp.results.map((charge) => chargeTransformer(charge)),
        }),
        getUnitAvailability: builder.mutation<
        RapidStorUnitAvailability,
        GetUnitAvailabilityRequestPayload
        >({
            query: ({ corpCode, locationCode, reservationId }) => ({
                url: `/api/v1/reservation/${reservationId}/unit-availability`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorUnitAvailability>,
            ) => resp.results,
        }),
        postScaReservationWithFeePrepare: builder.mutation<
        ScaPrepareResponse,
        PostScaReservationWithFeePrepare
        >({
            query: ({ locationCode, corpCode, payload }) => ({
                url: '/api/v1/sca/prepare/reservation-fee',
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<ScaPrepareResponse>) => resp.results,
        }),
        postScaMoveInPrepare: builder.mutation<
        ScaPrepareResponse,
        PostScaMoveInPrepare
        >({
            query: ({ locationCode, corpCode, payload }) => ({
                url: '/api/v1/sca/prepare/move-in',
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<ScaPrepareResponse>) => resp.results,
        }),
        postScaMoveInBillingInfoUpdate: builder.mutation<
        ScaPrepareResponse,
        PostScaBillingInfoUpdate
        >({
            query: ({ locationCode, corpCode, payload }) => ({
                url: '/api/v1/sca/prepare/move-in/billing-info-update',
                method: 'POST',
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<ScaPrepareResponse>) => resp.results,
        }),
        postScaMoveInPayment: builder.mutation<
        ScaPrepareResponse,
        PostScaMoveInPrepare
        >({
            query: ({ locationCode, corpCode, payload }) => ({
                url: '/api/v1/sca/prepare/move-in/payment',
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<ScaPrepareResponse>) => resp.results,
        }),
        getWizardInitialisation: builder.query<
        RapidStorWizardInit,
        PostWizardInitRequestPayload
        >({
            query: ({
                locationCode, corpCode, wizard, reservationToken, language, externalData,
            }) => ({
                url: '/api/v2/wizard/init',
                method: HttpMethod.POST,
                body: {
                    wizard,
                    reservationToken,
                    language,
                    externalData,
                },
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorWizardInit>) => resp.results,
        }),
        getMerchandise: builder.query<RapidStorMerchandise, BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: '/api/v1/merchandise',
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorMerchandise>) => resp.results,
        }),
        getBoxShopCoupon: builder.mutation({
            query: ({ corpToken, siteToken, code }) => ({
                url: `/rapidstor/${corpToken}/${siteToken}/api/boxshop/coupon/${code}`,
                method: HttpMethod.GET,
            }),
            transformResponse: (resp: RapidStorBoxShopCouponResponse) => resp,
        }),
        postApplyCoupon: builder.mutation({
            query: ({ corpToken, siteToken, payload }) => ({
                url: `/rapidstor/${corpToken}/${siteToken}/api/deal/coupon/apply`,
                method: HttpMethod.POST,
                body: payload,
            }),
            transformResponse: (resp: RapidStorCouponRatesResponse) => resp,
        }),
        getCouponRates: builder.mutation<
        RapidStorCouponRatesResponse,
        GetCouponRatesRequestPayload
        >({
            query: ({
                couponCode, unitTypeId, corpCode, locationCode,
            }) => ({
                url: `/api/v1/coupon/get-rates?code=${couponCode}&unitTypeId=${unitTypeId}`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorCouponRatesResponse>,
            ) => resp.results,
        }),
        postMoveIn: builder.mutation({
            query: ({
                locationCode, corpCode, payload, token,
            }) => ({
                url: `/api/v1/move-in/${token}`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorMoveInResponse>) => resp,
            transformErrorResponse: (resp: {
                status: number;
                data: RapidStorErrorResponse<string>;
            }) => resp.data.error,
        }),
        postReservationSaveDocuments: builder.mutation<
        undefined,
        PostReservationSaveDocumentsPayload
        >({
            query: ({
                documents,
                corpToken,
                locationToken,
                reservationToken,
                tenantId,
            }) => ({
                url: `/rapidstor/${corpToken}/${locationToken}/api/reservation/${reservationToken}/${tenantId}/save-documents`,
                method: HttpMethod.POST,
                headers: {
                    'Content-Type': undefined,
                },
                body: documents,
            }),
        }),
        getMarketingSources: builder.query<
        Record<string, MarketingSource[]>,
        BaseApiRequestPayload
        >({
            query: ({ locationCode, corpCode }) => ({
                url: `/api/v1/locations/${locationCode}/marketing-sources`,
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<MarketingSource[]>) => groupByLocationCode(resp.results),
        }),
        postMarketingSurvey: builder.mutation<
        string,
        PostMarketingSurveyRequestPayload
        >({
            query: ({
                locationCode, corpCode, tenantId, payload,
            }) => ({
                url: `/api/v1/tenant/${tenantId}/marketing`,
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<string>) => resp.results,
        }),
        getOAuthIntegrations: builder.query<OAuthIntegration[], BaseApiRequestPayload>({
            query: ({ locationCode, corpCode }) => ({
                url: '/api/v1/mtas/oauth/integrations',
                method: HttpMethod.GET,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<OAuthIntegration[]>) => resp.results,
        }),
        postOAuthIntegrationRequest: builder.mutation<OAuthIntegrationResponse, OAuthIntegrationRequestPayload>({
            query: ({ locationCode, corpCode, payload }) => ({
                url: '/api/v1/mtas/oauth/request',
                method: HttpMethod.POST,
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<OAuthIntegrationResponse>) => resp.results,
            transformErrorResponse: (resp: { status: number, data: RapidStorErrorResponse<string> }) => resp.data.error,
        }),
    }),
});

export const {
    useGetCorpQuery,
    useGetReservationQuery,
    useGetReservationBySitelinkQuery,
    useSearchReservationsQuery,

    usePostContactMutation,
    usePostReservationMutation,
    usePostSaveQuoteMutation,
    usePostTrackEventMutation,

    usePatchReservationMutation,
    usePostReservationFeeMutation,

    useGetMoveInCostRetrieveMutation,
    useGetChargesQuery,
    useGetUnitAvailabilityMutation,

    usePostScaReservationWithFeePrepareMutation,
    usePostScaMoveInPrepareMutation,
    usePostScaMoveInBillingInfoUpdateMutation,
    usePostScaMoveInPaymentMutation,

    useGetWizardInitialisationQuery,
    usePostMoveInMutation,
    useGetMerchandiseQuery,

    usePostApplyCouponMutation,
    useGetCouponRatesMutation,
    useGetBoxShopCouponMutation,

    usePostReservationSaveDocumentsMutation,
    useGetMarketingSourcesQuery,
    usePostMarketingSurveyMutation,

    useGetOAuthIntegrationsQuery,
    usePostOAuthIntegrationRequestMutation,

    useGetLocationByCodeQuery,
    useGetUnitListByTypeQuery,
    useGetPersonasQuery,
} = rapidstorApi;
